import { IRosterSelectionStrategy } from './IRosterSelectionStrategy';
import { IVirtualizedRoster, RosterElement } from '../VirtualizedRosterTypes';
import { DateTime } from '@app/AppConstants/DateTime';
import DateTimeService from '@app/Services/DateTimeService';

export type SelectionStrategyConstructor = new (roster: IVirtualizedRoster, gridRef: React.RefObject<HTMLDivElement>) => IRosterSelectionStrategy;

export abstract class BaseRosterSelectionStrategy implements IRosterSelectionStrategy {
    protected cellInnerClass: string = 'person-grid-duty-info';

    protected _roster: IVirtualizedRoster;
    protected _gridRef: React.RefObject<HTMLDivElement>;

    constructor(roster: IVirtualizedRoster, gridRef: React.RefObject<HTMLDivElement>) {
        this._roster = roster;
        this._gridRef = gridRef;
    }

    protected getElementFromDom(domElement: Element): RosterElement {
        let cell = domElement;
        if (cell.classList.contains(this.cellInnerClass) && cell.parentElement) {
            cell = cell.parentElement;
        }

        const personDate = cell.getAttribute('data-date') ?? '';
        const personId = cell.getAttribute('data-person-id') ?? '';
        const dutyDay = DateTimeService.parse(personDate, DateTime.jsonDateFormat);
        return {
            personId: personId,
            dateUtc: dutyDay.getTime()
        };
    }

    protected getDomElement(element: RosterElement) {
        const grid = this._gridRef.current;
        if (!grid) {
            return null;
        }

        return grid.querySelector(`[data-person-id='${element.personId}'][data-date='${DateTimeService.toJsonFormat(new Date(element.dateUtc))}']`);
    }

    protected getIndexAttr(target: Element, attrName: string): number {
        let cell = target;
        if (cell.classList.contains(this.cellInnerClass) && cell.parentElement) {
            cell = cell.parentElement;
        }

        const attribute = cell.attributes.getNamedItem(attrName);
        return (attribute && +attribute.value) || 0;
    }

    protected selectItem(item: Element) {
        let cell = item;
        if (cell.classList.contains(this.cellInnerClass) && cell.parentElement) {
            cell = cell.parentElement;
        }
        cell.classList.add('selected');
    }

    protected deselectItem(item: Element) {
        let cell = item;
        if (cell.classList.contains(this.cellInnerClass) && cell.parentElement) {
            cell = cell.parentElement;
        }
        cell.classList.remove('selected');
    }

    protected convertToRosterElements(domElements: Element[]) {
        const list: RosterElement[] = [];
        domElements.forEach((item) => {
            const rosterCell: RosterElement = this.getElementFromDom(item);
            const hasDublicate: boolean = list.some((el) => el.personId === rosterCell.personId && el.dateUtc === rosterCell.dateUtc);
            if (!hasDublicate) {
                list.push(rosterCell);
            }
        });
        return list;
    }

    protected getCellsRange(rangeStartCell: Element, rangeEndCell: Element) {
        const result: Element[] = [];

        const grid = this._gridRef.current;
        if (!grid) return result;

        const rowStartIndex = this.getIndexAttr(rangeStartCell, 'aria-rowindex');
        const colStartIndex = this.getIndexAttr(rangeStartCell, 'aria-colindex');

        const rowEndIndex = this.getIndexAttr(rangeEndCell, 'aria-rowindex');
        const colEndIndex = this.getIndexAttr(rangeEndCell, 'aria-colindex');

        const rowStart = rowStartIndex < rowEndIndex ? rowStartIndex : rowEndIndex;
        const rowEnd = rowStartIndex < rowEndIndex ? rowEndIndex : rowStartIndex;
        const colStart = colStartIndex < colEndIndex ? colStartIndex : colEndIndex;
        const colEnd = colStartIndex < colEndIndex ? colEndIndex : colStartIndex;

        for (let i = rowStart; i <= rowEnd; i++) {
            const cells = grid.querySelectorAll(`[aria-rowindex='${i}']`);
            cells.forEach((cell) => {
                const colIndexAttr = cell.attributes.getNamedItem('aria-colindex');
                const cellI = colIndexAttr ? +colIndexAttr.value : 0;
                if (cellI >= colStart && cellI <= colEnd) {
                    result.push(cell);
                }
            });
        }

        return result;
    }

    protected selectRange(rangeStartCell: Element, rangeEndCell: Element) {
        const cells = this.getCellsRange(rangeStartCell, rangeEndCell);

        cells.forEach((cell) => {
            this.selectItem(cell);
        });
    }

    abstract mouseDownHandler(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;

    abstract mouseMoveHandler(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;

    abstract documentMouseUpHandler(): void;

    protected getSelectedCells(): Element[] {
        const result: Element[] = [];
        if (this._gridRef.current) {
            this._gridRef.current.querySelectorAll(`.selected .${this.cellInnerClass}`).forEach((c) => {
                result.push(c);
            });
        }
        return result;
    }

    public updateDomSelection(cells: RosterElement[]): void {
        const targetCells = cells.slice(0);

        this.getSelectedCells().forEach((domElement) => {
            const rosterElement = this.getElementFromDom(domElement);
            const targetCellIndex = targetCells.findIndex((c) => c.personId === rosterElement.personId && c.dateUtc === rosterElement.dateUtc);
            if (targetCellIndex === -1) {
                this.deselectItem(domElement);
                return;
            }

            targetCells.splice(targetCellIndex, 1);
        });

        targetCells.forEach((c) => {
            const domElement = this.getDomElement(c);
            domElement && this.selectItem(domElement);
        });
    }
}
